import { z } from 'zod';

import { formatDuration } from '@/features/time-study/presentation';
import type { StageKind } from '@/lib/stage-kind';

// Estándar de la etapa en el editor (spec 2026-10-14 §7): contrato de
// GET /production-standards/applicable y la lectura del trabajo de la etapa contra él.
// Es la misma medida del Estudio de tiempos: esfuerzo de toda la etapa vs. su estándar.

const STANDARD_STAGES = ['elaboracion', 'revision', 'aprobacion', 'capacitacion'] as const;
type StandardStage = (typeof STANDARD_STAGES)[number];

const stageStandardSchema = z
  .object({
    versionId: z.string(),
    standardMinutes: z.number().int().nonnegative().nullable(),
    effectiveFrom: z.string(),
  })
  .nullable();

const applicableSchema = z.object({
  stages: z.object({
    elaboracion: stageStandardSchema,
    revision: stageStandardSchema,
    aprobacion: stageStandardSchema,
    capacitacion: stageStandardSchema,
  }),
  totalMinutes: z.number().int().nonnegative().nullable(),
});
export type ApplicableStandards = z.infer<typeof applicableSchema>;

export const applicableResponseSchema = z.object({
  success: z.literal(true),
  data: applicableSchema,
});

function isStandardStage(stage: StageKind): stage is StandardStage {
  return (STANDARD_STAGES as readonly string[]).includes(stage);
}

/** Minutos del estándar de la etapa; null si no tiene o si la etapa no lleva estándar. */
export function standardFor(view: ApplicableStandards, stage: StageKind): number | null {
  return isStandardStage(stage) ? (view.stages[stage]?.standardMinutes ?? null) : null;
}

export interface StandardProgress {
  /** Porcentaje del estándar consumido (redondeado), para leerlo. */
  pct: number;
  /** Ancho de la barra: nunca más de 100. */
  barPct: number;
  over: boolean;
  /** "2 h 10 min de 4 h". */
  text: string;
  /** "Supera el estándar por 30 min", o null si no lo supera. */
  overText: string | null;
}

export function standardProgress(effortSeconds: number, standardMinutes: number): StandardProgress {
  const standardSeconds = standardMinutes * 60;
  const effort = Math.max(0, effortSeconds);
  const over = effort > standardSeconds;
  const pct =
    standardSeconds === 0 ? (over ? 100 : 0) : Math.round((effort / standardSeconds) * 100);
  return {
    pct,
    barPct: Math.min(100, pct),
    over,
    text: `${formatDuration(effort)} de ${formatDuration(standardSeconds)}`,
    overText: over ? `Supera el estándar por ${formatDuration(effort - standardSeconds)}` : null,
  };
}
